import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailEntity from "@mapmycustomers/shared/types/email/EmailEntity";
import EmailRecipientAddress from "@mapmycustomers/shared/types/email/EmailRecipientAddress";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import EmailLink from "@app/component/email/EmailLink";
import { fetchSentEmailEntities } from "@app/store/email/actions";
import normalizeEmail from "@app/util/email/normalizeEmail";

import styles from "./SentEmailRecipients.module.scss";

const getEmailRecipientEntity = (
  emailRecipient: EmailRecipientAddress,
  { emailEntities, inaccessibleEmailEntities }: EmailData
) => {
  const normalizedRecipientEmail = normalizeEmail(emailRecipient.email);

  const emailEntity = emailEntities.find(
    (emailEntity) => normalizeEmail(emailEntity.email) === normalizedRecipientEmail
  );

  if (emailEntity) {
    return emailEntity;
  }

  const inaccessibleEmailEntity = inaccessibleEmailEntities.find(
    (emailEntity) => normalizeEmail(emailEntity.email) === normalizedRecipientEmail
  );

  if (inaccessibleEmailEntity) {
    return null;
  }

  return undefined;
};

interface EmailData {
  emailEntities: EntitiesSupportedByEmailFeature[];
  inaccessibleEmailEntities: EmailEntity[];
}

interface SentEmailRecipientsProps {
  allowSendViaEmailClient?: boolean;
  allowSendViaMmcEmail?: boolean;
  email: Email;
}

const SentEmailRecipients = ({
  allowSendViaEmailClient,
  allowSendViaMmcEmail,
  email,
  onSentEmailEntitiesFetch,
}: SentEmailRecipientsProps & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [emailData, setEmailData] = useState<
    | {
        emailEntities: EntitiesSupportedByEmailFeature[];
        inaccessibleEmailEntities: EmailEntity[];
      }
    | undefined
  >(undefined);

  useEffect(() => {
    setEmailData(undefined);
  }, [email]);

  const fetchEmailRecipientEntity = useDynamicCallback((emailRecipient: EmailRecipientAddress) => {
    return new Promise<EntitiesSupportedByEmailFeature | null | undefined>((resolve, reject) => {
      if (emailData) {
        resolve(getEmailRecipientEntity(emailRecipient, emailData));
      }

      onSentEmailEntitiesFetch({
        callback: ({ emailEntities, inaccessibleEmailEntities }) => {
          const emailData: EmailData = { emailEntities, inaccessibleEmailEntities };
          setEmailData(emailData);
          resolve(getEmailRecipientEntity(emailRecipient, emailData));
        },
        emailId: email.id,
        failureCallback: () => {
          reject();
        },
      });
    });
  });

  return (
    <div className={styles.container}>
      <div className={styles.to}>
        {intl.formatMessage({
          id: "email.sentEmailRecipients.to",
          defaultMessage: "To",
          description: "Email recipients - to",
        })}
      </div>
      <div>
        {email.recipientAddress.map((recipient) => (
          <div className={styles.recipient} key={`${recipient.email} ${recipient.name}`}>
            <span>{recipient.name}</span>
            <span>
              (
              <EmailLink
                allowSendViaEmailClient={allowSendViaEmailClient}
                allowSendViaMmcEmail={allowSendViaMmcEmail}
                className={styles.email}
                email={recipient.email}
                onFetchEntity={() => fetchEmailRecipientEntity(recipient)}
                showIcon={false}
              />
              )
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onSentEmailEntitiesFetch: fetchSentEmailEntities,
};

export default connect(null, mapDispatchToProps)(SentEmailRecipients);
